import { Injectable } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';

/** Message columns the server-side Translate Message Content action will translate. */
export type TranslatableField = 'MessageContent' | 'GeneratedReplyContent' | 'ApprovedReplyContent' | 'SentContent';

export interface MessageTranslation {
  success: boolean;
  /** Full translation, structure and markup preserved. */
  translatedText?: string;
  /** Translated subject line (inbound message only), or null. */
  translatedSubject?: string | null;
  /** Language the source was written in, as an English language name (e.g. "Spanish"). */
  detectedLanguage?: string;
  /** User-facing failure message. */
  error?: string;
}

/**
 * Read-only message translation for staff (translate-view feature).
 *
 * Thin client over the server-side "Translate Message Content" action: the browser sends only
 * the message ID, which column, and the target language. The server reads the text from the
 * database as the requesting user and runs the Izzy Message Translator prompt with the
 * organization's own AI configuration and vendor keys. Nothing is persisted server-side; results
 * are cached here for the session so toggling between original and translation is free.
 *
 * The last language a user picked is remembered per browser (localStorage) purely as a
 * convenience; the default is English.
 */
@Injectable({ providedIn: 'root' })
export class IzzyTranslationService {
  /** Languages offered in the picker. Names are passed to the model as-is. */
  public static readonly LANGUAGES: ReadonlyArray<string> = [
    'English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian', 'Dutch', 'Polish', 'Swedish',
    'Norwegian', 'Danish', 'Finnish', 'Greek', 'Czech', 'Hungarian', 'Romanian', 'Ukrainian', 'Russian',
    'Turkish', 'Arabic', 'Hebrew', 'Hindi', 'Chinese (Simplified)', 'Chinese (Traditional)', 'Japanese',
    'Korean', 'Vietnamese', 'Thai', 'Indonesian',
  ];
  public static readonly DEFAULT_LANGUAGE = 'English';

  private static readonly ACTION_NAME = 'Translate Message Content';
  private static readonly LAST_LANGUAGE_KEY = 'izzy.translate.lastLanguage';
  /** Session cache cap; translations are small, but never let it grow unbounded. */
  private static readonly MAX_CACHE_ENTRIES = 200;

  private actionIdLookup: Promise<string | null> | null = null;
  private readonly cache = new Map<string, MessageTranslation>();

  /** The language the user picked last time in this browser, else English. */
  public get lastLanguage(): string {
    try {
      const stored = localStorage.getItem(IzzyTranslationService.LAST_LANGUAGE_KEY);
      if (stored && IzzyTranslationService.LANGUAGES.includes(stored)) {
        return stored;
      }
    } catch {
      // Storage unavailable (private mode, blocked) — fall through to the default.
    }
    return IzzyTranslationService.DEFAULT_LANGUAGE;
  }

  public set lastLanguage(language: string) {
    try {
      localStorage.setItem(IzzyTranslationService.LAST_LANGUAGE_KEY, language);
    } catch {
      // Per-browser convenience only; ignore storage failures.
    }
  }

  /**
   * Translates one column of a message. `currentText` is the text the caller is displaying —
   * it is NOT sent to the server (the server reads the column itself) but it keys the cache, so
   * an edited draft never serves a stale translation.
   */
  public async translate(
    messageId: string,
    field: TranslatableField,
    targetLanguage: string,
    currentText: string
  ): Promise<MessageTranslation> {
    const cacheKey = `${messageId}|${field}|${targetLanguage}|${currentText.length}|${this.hash(currentText)}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const actionId = await this.getActionId();
    if (!actionId) {
      return { success: false, error: 'Translation is not installed on this server.' };
    }

    try {
      const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
      const result = await client.RunAction(actionId, [
        { Name: 'ChannelMessageID', Value: messageId, Type: 'Input' },
        { Name: 'Field', Value: field, Type: 'Input' },
        { Name: 'TargetLanguage', Value: targetLanguage, Type: 'Input' },
      ] as never);
      if (!result?.Success) {
        return { success: false, error: result?.Message || 'Translation failed.' };
      }
      const outputs = this.readOutputs(result.Result);
      const translatedText = outputs.get('TranslatedText');
      if (!translatedText) {
        return { success: false, error: 'Translation returned no text.' };
      }
      const translation: MessageTranslation = {
        success: true,
        translatedText,
        translatedSubject: outputs.get('TranslatedSubject') || null,
        detectedLanguage: outputs.get('DetectedLanguage') || 'Unknown',
      };
      this.remember(cacheKey, translation);
      return translation;
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Translation failed.' };
    }
  }

  /** Resolves (once) the action's ID by name; a null result is not cached so a later install is picked up. */
  private getActionId(): Promise<string | null> {
    if (!this.actionIdLookup) {
      this.actionIdLookup = (async () => {
        try {
          const rv = new RunView();
          const res = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name = '${IzzyTranslationService.ACTION_NAME}'`,
            Fields: ['ID'],
            MaxRows: 1,
          });
          const id = (res.Success ? res.Results : [])[0]?.ID ?? null;
          if (!id) {
            this.actionIdLookup = null;
          }
          return id;
        } catch {
          this.actionIdLookup = null;
          return null;
        }
      })();
    }
    return this.actionIdLookup;
  }

  /**
   * ActionResult types Result as the result-code entity, but the GraphQL client actually parses
   * the action's Output params into it. MJServer runs that array through CopyScalarsAndArrays,
   * which can turn it into an index-keyed OBJECT ({"0": {...}}), so accept both shapes.
   */
  private readOutputs(raw: unknown): Map<string, string> {
    const candidates: unknown[] = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object' ? Object.values(raw) : [];
    const outputs = new Map<string, string>();
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue;
      const { Name, Value } = candidate as { Name?: unknown; Value?: unknown };
      if (typeof Name === 'string' && typeof Value === 'string') {
        outputs.set(Name, Value);
      }
    }
    return outputs;
  }

  private remember(key: string, translation: MessageTranslation): void {
    if (this.cache.size >= IzzyTranslationService.MAX_CACHE_ENTRIES) {
      this.cache.clear();
    }
    this.cache.set(key, translation);
  }

  /** djb2 — cheap content fingerprint for the cache key (not security-relevant). */
  private hash(text: string): string {
    let h = 5381;
    for (let i = 0; i < text.length; i++) {
      h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(36);
  }
}
